/**
 * ambient_herds — the PURE rules of the grazing herds (ambient life wave, 2026-09-29; the research's R12 "grazing herds
 * at 0.3–2 km on plains").
 *
 * Three peaceful species, each of one biome: the COPPERHORN of the regolith plains, the FENBACK of the moss wetland and
 * the WOOLCRAG of the rocky highlands (models `prop:herd_<key>`, Meshy 7 ultra, re-baked; every head faces +Z).
 *
 * WHERE: a herd lives in a 1.6 km world cell chosen by the world seed alone — every client sees the same herd in the
 * same place — and only where its biome is, on open ground (not forest, not water, not a road, not near a town).
 * Its centre wanders a slow closed loop around that home (a 15–25 minute period, 140 m), so a herd seen twice has moved.
 *
 * HOW: grazers GRAZE (head down, a step now and then), catch up with the herd when they fall behind, look up when a
 * person comes within `alertM`, and the whole herd trots AWAY when one comes within `fleeM` — the way real grazers keep
 * their distance. That per-client flight is an offset that relaxes back home once nobody has been near for `calmS`.
 * Everything here is deterministic given an rng; AtlasHerdsModule owns the world, the draws and the shader.
 */

export type HerdSpeciesKey = 'copperhorn' | 'fenback' | 'woolcrag';
export const HERD_SPECIES_KEYS: readonly HerdSpeciesKey[] = ['copperhorn', 'fenback', 'woolcrag'];

export interface HerdSpecies {
  key: HerdSpeciesKey;
  /** The one biome it lives in (shared/terrain biomeAt). */
  biome: string;
  /** Nose-to-tail length (m) — the model is scaled to it. */
  lengthM: number;
  /** Animals in a herd, inclusive. */
  herd: readonly [number, number];
  /** Walking and trotting speed (m/s). */
  walk: number;
  trot: number;
  /** Mean distance between neighbours in the herd (m). */
  spacing: number;
  /** A person inside this raises the heads; inside `fleeM` the herd trots away (m). */
  alertM: number;
  fleeM: number;
  /** Stride length at a walk (m) — the gait's step rate is speed ÷ stride. */
  stride: number;
  /** Head-down angle when grazing (rad) and head-up when alert (negative = raised). */
  grazeNeck: number;
  alertNeck: number;
}

export const HERD_SPECIES: Readonly<Record<HerdSpeciesKey, HerdSpecies>> = {
  copperhorn: { key: 'copperhorn', biome: 'regolith_plains', lengthM: 2.3, herd: [7, 14], walk: 1.15, trot: 7.5, spacing: 6, alertM: 150, fleeM: 95, stride: 1.6, grazeNeck: 1.0, alertNeck: -0.18 },
  fenback: { key: 'fenback', biome: 'moss_wetland', lengthM: 3.2, herd: [5, 10], walk: 0.85, trot: 4.8, spacing: 7, alertM: 110, fleeM: 70, stride: 1.4, grazeNeck: 0.7, alertNeck: -0.12 },
  woolcrag: { key: 'woolcrag', biome: 'rocky_highlands', lengthM: 2.0, herd: [6, 12], walk: 0.95, trot: 5.5, spacing: 5, alertM: 130, fleeM: 85, stride: 1.1, grazeNeck: 0.75, alertNeck: -0.15 },
};

export const HERD = {
  /** World cell size (m): at most one herd per cell. */
  cell: 1600,
  /** A cell of the right biome holds a herd this often. */
  chance: 0.45,
  /** Herds are drawn within this range of the viewer (m) — the module also clamps to the terrain window. */
  drawM: 2000,
  /** Herds are dropped past this (m). */
  despawnM: 2400,
  /** How far the herd's centre wanders from its home (m) and the loop's period band (s). */
  wander: 140,
  period: [900, 1500] as const,
  /** Open ground only: forest density under this, this far above the sea (m), this far outside a town's plateau (m). */
  forestMax: 0.25,
  seaClear: 4,
  plateauClear: 250,
  /** The relief of the home's 80 m neighbourhood may not exceed this (m) — herds keep to gentle ground. */
  reliefMax: 12,
  /** The road mask (terrain_stamp.roadSurfaceMask) must stay under this at home. */
  roadMax: 0.12,
  /** Animals drawn at particleCap 1. */
  maxAnimals: 120,
  /** A frightened herd relaxes back home once nobody has come within fleeM + 30 for this long (s). */
  calmS: 40,
  /** A herd flees at most this far from its wandering home (m). */
  fleeMax: 700,
} as const;

// ─── the seeded field ─────────────────────────────────────────────────────────

/** FNV-1a over a string, then fmix32 — geometry-grade hashing (the galaxy's HASH LAW: bare FNV is arithmetic in its last character). */
export function herdHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}

/** A seeded rng (mulberry32). */
export function herdRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

export interface HerdSite {
  /** Stable id: `<cx>:<cz>`. */
  id: string;
  cx: number;
  cz: number;
  homeX: number;
  homeZ: number;
  /** The per-site seed every animal of the herd derives from. */
  seed: number;
  /** Animals in the herd (the species' band); the module may draw fewer under the tier's cap. */
  count: number;
  /** The wander loop: two periods (s) and two phases. */
  pA: number; pB: number; phA: number; phB: number;
}

/** The candidate herd of a cell, or null when the dice say none — WITHOUT the biome / terrain tests (the module's oracles). */
export function herdCandidate(worldSeed: string, cx: number, cz: number): HerdSite | null {
  const seed = herdHash(`${worldSeed}|herd|${cx}|${cz}`);
  const r = herdRng(seed);
  if (r() >= HERD.chance) return null;
  const homeX = (cx + 0.2 + 0.6 * r()) * HERD.cell;
  const homeZ = (cz + 0.2 + 0.6 * r()) * HERD.cell;
  const count = r(); // the band is applied once the species is known
  return {
    id: `${cx}:${cz}`, cx, cz, homeX, homeZ, seed, count,
    pA: HERD.period[0] + r() * (HERD.period[1] - HERD.period[0]),
    pB: HERD.period[0] + r() * (HERD.period[1] - HERD.period[0]),
    phA: r() * Math.PI * 2, phB: r() * Math.PI * 2,
  };
}

/** The herd size for a species from the candidate's 0..1 roll. */
export function herdSize(sp: HerdSpecies, roll01: number): number {
  return sp.herd[0] + Math.min(sp.herd[1] - sp.herd[0], Math.floor(roll01 * (sp.herd[1] - sp.herd[0] + 1)));
}

/** The world cells whose herds could be in range of (px, pz). */
export function herdCellsNear(px: number, pz: number, radius: number): Array<[number, number]> {
  const c = HERD.cell, out: Array<[number, number]> = [];
  const x0 = Math.floor((px - radius) / c), x1 = Math.floor((px + radius) / c);
  const z0 = Math.floor((pz - radius) / c), z1 = Math.floor((pz + radius) / c);
  for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) out.push([cx, cz]);
  return out;
}

/** What the module can tell about a point — the terrain, water and town oracles, injected so the rules stay pure. */
export interface HerdGround {
  biome: string;
  forestDensity: number;
  /** Ground height above the sea (m). */
  aboveSea: number;
  /** True when a lake or river covers the point. */
  wet: boolean;
  /** Distance to the nearest town plateau's rim (m); negative inside. */
  toTown: number;
  /** Road mask 0..1. */
  road: number;
  /** Height range over the 80 m neighbourhood (m). */
  relief: number;
}

/** The species that may live on this ground, or null (with the reason, for the diagnostics). */
export function herdSpeciesFor(g: HerdGround): { species: HerdSpeciesKey | null; why: string } {
  const sp = HERD_SPECIES_KEYS.find((k) => HERD_SPECIES[k].biome === g.biome) ?? null;
  if (!sp) return { species: null, why: `biome ${g.biome} has no herd` };
  if (g.forestDensity >= HERD.forestMax) return { species: null, why: 'forest' };
  if (g.aboveSea < HERD.seaClear) return { species: null, why: 'too near the sea' };
  if (g.wet) return { species: null, why: 'water' };
  if (g.toTown < HERD.plateauClear) return { species: null, why: 'near a town' };
  if (g.road >= HERD.roadMax) return { species: null, why: 'a road' };
  if (g.relief > HERD.reliefMax) return { species: null, why: 'rough ground' };
  return { species: sp, why: '' };
}

/** The herd's wandering centre at world time t (s) — the same on every client. */
export function herdCentre(site: HerdSite, tS: number): { x: number; z: number; hx: number; hz: number } {
  const a = (tS / site.pA) * Math.PI * 2 + site.phA, b = (tS / site.pB) * Math.PI * 2 + site.phB;
  const x = site.homeX + Math.cos(a) * HERD.wander, z = site.homeZ + Math.sin(b) * HERD.wander * 0.8;
  // the heading of the drift (its derivative), for the animals' idle facing
  const hx = -Math.sin(a) * (Math.PI * 2 / site.pA), hz = Math.cos(b) * (Math.PI * 2 / site.pB) * 0.8;
  const hl = Math.hypot(hx, hz) || 1;
  return { x, z, hx: hx / hl, hz: hz / hl };
}

/** Animal i's resting slot relative to the herd centre: a golden-angle spiral, jittered per animal. */
export function herdSlot(site: HerdSite, sp: HerdSpecies, i: number): { ox: number; oz: number } {
  const r = herdRng(site.seed ^ Math.imul(i + 1, 0x9e3779b1));
  const rad = sp.spacing * Math.sqrt(i + 0.5) * (0.75 + 0.5 * r());
  const ang = i * 2.399963 + site.seed * 1e-9 + (r() - 0.5) * 0.9;
  return { ox: Math.cos(ang) * rad, oz: Math.sin(ang) * rad };
}

// ─── one animal ──────────────────────────────────────────────────────────────

export type GrazerState = 'graze' | 'shift' | 'walk' | 'alert' | 'flee';

export interface Grazer {
  i: number;
  x: number; z: number;
  /** Heading (rad): forward is (sin yaw, cos yaw) — the model's head faces +Z. */
  yaw: number;
  speed: number;
  state: GrazerState;
  /** Seconds left in the current graze / shift. */
  timer: number;
  /** A nudge target for a shift (a step or two while grazing). */
  tx: number; tz: number;
  /** The neck angle drawn (rad, + = head down) and the gait phase (rad) with its previous value (the motion blur). */
  neck: number;
  gait: number;
  gaitPrev: number;
  /** 0..1 how much the legs swing (0 standing). */
  swing: number;
  /** Per-animal pace variety. */
  pace: number;
}

export function makeGrazer(site: HerdSite, sp: HerdSpecies, i: number, cx: number, cz: number): Grazer {
  const r = herdRng(site.seed ^ Math.imul(i + 7, 0x85ebca6b));
  const s = herdSlot(site, sp, i);
  return {
    i, x: cx + s.ox, z: cz + s.oz, yaw: r() * Math.PI * 2, speed: 0, state: 'graze', timer: 2 + r() * 8,
    tx: cx + s.ox, tz: cz + s.oz, neck: sp.grazeNeck * (0.6 + 0.4 * r()), gait: r() * Math.PI * 2, gaitPrev: 0, swing: 0,
    pace: 0.85 + 0.3 * r(),
  };
}

/** The shortest signed turn from a to b (rad). Closed form — never a loop on an unbounded input (THE HOSTILE-YAW LAW). */
export function turnTo(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2; else if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export interface HerdThreat { x: number; z: number }

/**
 * One step of one animal. `slotX/slotZ` is where the herd wants it (centre + flight + slot); `fleeing` is the herd's
 * state; `near` the nearest person (or null). Mutates the animal; returns nothing.
 */
export function stepGrazer(g: Grazer, sp: HerdSpecies, dt: number, slotX: number, slotZ: number, fleeing: boolean, near: HerdThreat | null, rng: () => number): void {
  const nearD = near ? Math.hypot(near.x - g.x, near.z - g.z) : Infinity;
  const off = Math.hypot(slotX - g.x, slotZ - g.z);
  let want: GrazerState = g.state;
  if (fleeing) want = 'flee';
  else if (nearD < sp.alertM) want = 'alert';
  else if (off > sp.spacing * 1.4) want = 'walk';
  else if (g.state === 'flee' || g.state === 'alert' || g.state === 'walk') { want = 'graze'; g.timer = 1 + rng() * 4; }
  if (want !== g.state) g.state = want;

  let targetSpeed = 0, faceYaw = g.yaw, neckTarget = sp.grazeNeck, tx = slotX, tz = slotZ;
  switch (g.state) {
    case 'graze': {
      g.timer -= dt;
      if (g.timer <= 0) {
        // a step or two to fresh grass
        const a = rng() * Math.PI * 2, d = 0.8 + rng() * 2;
        g.tx = g.x + Math.cos(a) * d; g.tz = g.z + Math.sin(a) * d;
        g.state = 'shift'; g.timer = 1.5 + rng() * 2;
      }
      break;
    }
    case 'shift': {
      g.timer -= dt;
      tx = g.tx; tz = g.tz;
      targetSpeed = sp.walk * 0.55 * g.pace;
      neckTarget = sp.grazeNeck * 0.55;
      if (g.timer <= 0 || Math.hypot(g.tx - g.x, g.tz - g.z) < 0.25) { g.state = 'graze'; g.timer = 3 + rng() * 9; targetSpeed = 0; }
      break;
    }
    case 'walk':
      targetSpeed = sp.walk * 1.4 * g.pace;
      neckTarget = 0.05;
      break;
    case 'alert':
      targetSpeed = 0;
      neckTarget = sp.alertNeck;
      if (near) faceYaw = Math.atan2(near.x - g.x, near.z - g.z);
      break;
    case 'flee':
      targetSpeed = sp.trot * g.pace * Math.min(1, 0.4 + off / (sp.spacing * 2));
      neckTarget = sp.alertNeck * 0.5;
      break;
  }
  // move toward the target at the eased speed; face the way it moves
  const dx = tx - g.x, dz = tz - g.z, dl = Math.hypot(dx, dz);
  const accel = g.state === 'flee' ? 4 : 1.6;
  g.speed += Math.max(-accel * dt, Math.min(accel * dt, targetSpeed - g.speed));
  if (dl < 0.05 || g.speed < 0.02) { if (g.state !== 'alert') g.speed = Math.min(g.speed, dl / Math.max(dt, 1e-3)); }
  if (g.speed > 0.02 && dl > 0.05) {
    const step = Math.min(dl, g.speed * dt);
    g.x += (dx / dl) * step; g.z += (dz / dl) * step;
    if (g.state !== 'alert') faceYaw = Math.atan2(dx, dz);
  }
  const turnRate = g.state === 'flee' ? 3.2 : 1.4;
  const d = turnTo(g.yaw, faceYaw);
  g.yaw += Math.max(-turnRate * dt, Math.min(turnRate * dt, d));
  g.neck += (neckTarget - g.neck) * Math.min(1, dt * (g.state === 'alert' || g.state === 'flee' ? 4 : 1.5));
  // the gait: step rate by speed over stride; the swing grows with speed, trotting swings wider
  g.gaitPrev = g.gait;
  const hz = g.speed / Math.max(0.2, sp.stride * (g.state === 'flee' ? 1.35 : 1));
  g.gait = (g.gait + hz * Math.PI * 2 * dt) % (Math.PI * 200);
  const swingTarget = g.speed < 0.05 ? 0 : Math.min(0.62, 0.22 + 0.4 * (g.speed / sp.trot));
  g.swing += (swingTarget - g.swing) * Math.min(1, dt * 5);
}

/**
 * The herd's per-client flight: while a person is within fleeM (+30 m of hysteresis once running) it moves away from
 * them at the trot; `calm` counts the quiet seconds, and after calmS it walks back toward zero. Returns the new state.
 */
export interface HerdFlight { ox: number; oz: number; fleeing: boolean; calm: number }
export function stepFlight(f: HerdFlight, sp: HerdSpecies, dt: number, centreX: number, centreZ: number, near: HerdThreat | null): HerdFlight {
  const cx = centreX + f.ox, cz = centreZ + f.oz;
  const d = near ? Math.hypot(near.x - cx, near.z - cz) : Infinity;
  const trigger = f.fleeing ? sp.fleeM + 30 : sp.fleeM;
  if (near && d < trigger) {
    const ax = (cx - near.x) / (d || 1), az = (cz - near.z) / (d || 1);
    let ox = f.ox + ax * sp.trot * 0.9 * dt, oz = f.oz + az * sp.trot * 0.9 * dt;
    const m = Math.hypot(ox, oz);
    if (m > HERD.fleeMax) { ox *= HERD.fleeMax / m; oz *= HERD.fleeMax / m; }
    return { ox, oz, fleeing: true, calm: 0 };
  }
  const calm = f.calm + dt;
  if (calm < HERD.calmS) return { ox: f.ox, oz: f.oz, fleeing: false, calm };
  const m = Math.hypot(f.ox, f.oz);
  if (m < 0.5) return { ox: 0, oz: 0, fleeing: false, calm };
  const step = Math.min(m, sp.walk * 1.2 * dt);
  return { ox: f.ox - (f.ox / m) * step, oz: f.oz - (f.oz / m) * step, fleeing: false, calm };
}

/** How many animals the tier may draw (particleCap 0..1). */
export function herdAnimalCap(particleCap: number): number {
  const c = Math.max(0, Math.min(1, particleCap || 0));
  return c <= 0 ? 0 : Math.max(8, Math.round(HERD.maxAnimals * c));
}
